import { useCallback } from 'react'
import { Klass, LexicalNode, LexicalNodeReplacement } from 'lexical'

import { LEXICAL_NODES } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'

import { addToast } from '../../../store/toast/toastSlice'
import { useAppDispatch } from '../../../store/hooks'

import { AIAgentResultState, getEditorContent } from '../EditableAIAgentResult/EditableAIAgentResult'

import { extendCopyContentWithComplianceMessage, TEXT_TYPE, writeCopyContentToClipboard } from '../../../common/copy'
import { decorateEmailWithCompliance } from '../../../common/useMailtoDecorateWithCompliance'
import { useEffectiveConfig } from '../../../common/useEffectiveConfig'

interface UseAIAgentResultCopyProps {
  aiAgentResultState: AIAgentResultState | null
  lexicalNodes?: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement>
}

export const useAIAgentResultCopy = ({ aiAgentResultState, lexicalNodes = LEXICAL_NODES }: UseAIAgentResultCopyProps) => {
  const dispatch = useAppDispatch()

  const { emailComplianceMessage, copyComplianceMessage, accountIsFetching } = useEffectiveConfig()

  const handleCopy = useCallback(async () => {
    if (!aiAgentResultState || accountIsFetching) {
      return
    }

    const editorContent = getEditorContent(aiAgentResultState.editorState, lexicalNodes)
    if (!editorContent) {
      return
    }

    const copyContent = extendCopyContentWithComplianceMessage(editorContent, copyComplianceMessage)

    await writeCopyContentToClipboard(copyContent)

    dispatch(addToast({ variant: 'info', title: 'Copied', messages: ['Copied to Clipboard!'] }))
  }, [aiAgentResultState, accountIsFetching, lexicalNodes, copyComplianceMessage, dispatch])

  const handleMailToClicked = useCallback(() => {
    if (!aiAgentResultState || accountIsFetching) {
      return
    }
    const editorContent = getEditorContent(aiAgentResultState.editorState, lexicalNodes)
    if (!editorContent) {
      return
    }

    const body = decorateEmailWithCompliance(editorContent[TEXT_TYPE], emailComplianceMessage)

    window.location.href = `mailto:?body=${encodeURIComponent(body)}`
  }, [aiAgentResultState, accountIsFetching, lexicalNodes, emailComplianceMessage])

  return {
    handleCopy,
    handleMailToClicked,
  }
}
